import { useMemo, useState } from "react";
import { Card, CardContent, Stack, Typography, Box } from "@mui/material";
import ClauseGroupTreemap, {
  getClauseGroupDisplayName,
} from "../charts/ClauseGroupTreemap";
import ClauseGroupPieChart from "../charts/ClauseGroupPieChart";
import FiltersPanelClause from "../FiltersPanelClause";
import type { ClauseRow, ClauseFilters } from "../types";

// Given selected instrument types, return the set of matching DOCIDs
function getMatchingDocIds(
  rows: ClauseRow[],
  selectedTypes: string[],
): Set<string> | null {
  if (selectedTypes.length === 0) return null;
  const selected = new Set(selectedTypes.map((type) => type.trim()));

  const matchedDocIds = new Set<string>();
  for (const row of rows) {
    const docId = String(row.DOCID ?? "").trim();
    const loanOrBond = String(row.Loan_or_Bond ?? "")
      .replace(/^"|"$/g, "")
      .trim();

    if (docId && selected.has(loanOrBond)) {
      matchedDocIds.add(docId);
    }
  }

  return matchedDocIds;
}

type ClauseTabProps = {
  clauseRows: ClauseRow[];
};

const BLUE_PALETTE = [
  "#012852", // darkest
  "#01497c",
  "#01497c", // matching to simplify palette
  "#0066AA",
  "#0066AA", // matching to simplify palette
  "#5196c4", // lightest
];

export default function ClauseTab({ clauseRows }: ClauseTabProps) {
  const [selectedClauseGroup, setSelectedClauseGroup] = useState<string | null>(
    null,
  );

  // Derive filter option lists from data
  const creditorRegionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.CreditorGeographicRegion ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const borrowerRegionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.BorrowerGeographicRegion ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const creditorTypeColumns = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.pCreditorType ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const denominationColumns = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.inferred_denominations_readable ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const minYear = useMemo(
    () =>
      Math.min(
        ...clauseRows
          .map((r) => Number(r.DocumentYear))
          .filter((y) => !isNaN(y) && y > 0),
      ),
    [clauseRows],
  );
  const maxYear = useMemo(
    () =>
      Math.max(
        ...clauseRows
          .map((r) => Number(r.DocumentYear))
          .filter((y) => !isNaN(y) && y > 0),
      ),
    [clauseRows],
  );

  const [clauseFilters, setClauseFilters] = useState<ClauseFilters>({
    selectedInstrumentTypes: [],
    selectedCreditorRegions: [],
    selectedBorrowerRegions: [],
    selectedCreditorTypes: [],
    selectedDenominations: [],
    yearRange: [minYear, maxYear],
    completeDataOnly: false,
  });

  // Filter clauseRows by all active filters
  const filteredClauseRows = useMemo(() => {
    const matchingDocIds = getMatchingDocIds(
      clauseRows,
      clauseFilters.selectedInstrumentTypes,
    );
    return clauseRows.filter((row) => {
      if (matchingDocIds) {
        const docId = String(row.DOCID ?? "").trim();
        if (!matchingDocIds.has(docId)) return false;
      }
      if (clauseFilters.selectedCreditorRegions.length > 0) {
        const region = String(row.CreditorGeographicRegion ?? "").trim();
        if (!clauseFilters.selectedCreditorRegions.includes(region))
          return false;
      }
      if (clauseFilters.selectedBorrowerRegions.length > 0) {
        const region = String(row.BorrowerGeographicRegion ?? "").trim();
        if (!clauseFilters.selectedBorrowerRegions.includes(region))
          return false;
      }
      if (clauseFilters.selectedCreditorTypes.length > 0) {
        const type = String(row.pCreditorType ?? "").trim();
        if (!clauseFilters.selectedCreditorTypes.includes(type)) return false;
      }
      if (clauseFilters.selectedDenominations.length > 0) {
        const denom = String(row.inferred_denominations_readable ?? "").trim();
        if (!clauseFilters.selectedDenominations.includes(denom)) return false;
      }
      const year = Number(row.DocumentYear);
      if (
        !isNaN(year) &&
        (year < clauseFilters.yearRange[0] || year > clauseFilters.yearRange[1])
      )
        return false;
      if (clauseFilters.completeDataOnly) {
        const complete = String(row.complete_data ?? "").trim();
        if (complete !== "True") return false;
      }
      return true;
    });
  }, [clauseRows, clauseFilters]);

  // If the selected group is filtered out, reset it
  const effectiveSelectedGroup = useMemo(() => {
    if (!selectedClauseGroup) return null;
    const hasGroupInFilteredRows = filteredClauseRows.some((row) => {
      const group = String(row.clause_group ?? "")
        .replace(/^"|"$/g, "")
        .trim();
      return group === selectedClauseGroup;
    });
    return hasGroupInFilteredRows ? selectedClauseGroup : null;
  }, [selectedClauseGroup, filteredClauseRows]);

  const clauseHrRowsForSelection = useMemo(() => {
    if (!effectiveSelectedGroup) {
      return [] as ClauseRow[];
    }

    return filteredClauseRows.filter((row) => {
      const group = String(row.clause_group ?? "")
        .replace(/^"|"$/g, "")
        .trim();
      return group === effectiveSelectedGroup;
    });
  }, [filteredClauseRows, effectiveSelectedGroup]);

  const hasFilteredResults = filteredClauseRows.length > 0;

  return (
    <Stack spacing={2.5}>
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            The <strong>#PublicDebtIsPublic</strong> team&apos;s new annotation
            system is designed to help users identify and understand debt
            contract terms quickly. Use the interactive visualizations to
            explore what governments have promised their creditors.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {hasFilteredResults ? (
            <ClauseGroupPieChart rows={filteredClauseRows} />
          ) : (
            <Card sx={{ px: "1rem", py: "1rem", mb: "1rem"}}>
              <CardContent>
                <Typography sx={{ fontSize: "0.95rem", color: "#6b7280" }}>
                  No results match the selected filters. Try removing or
                  adjusting one or more filters.
                </Typography>
              </CardContent>
            </Card>
          )}
          <FiltersPanelClause
            filters={clauseFilters}
            setFilters={setClauseFilters}
            creditorRegionOptions={creditorRegionOptions}
            borrowerRegionOptions={borrowerRegionOptions}
            creditorTypeColumns={creditorTypeColumns}
            denominationColumns={denominationColumns}
            minYear={minYear}
            maxYear={maxYear}
            sticky={false}
            horizontal
            noCard={false}
          />
          {hasFilteredResults ? (
            <>
              <ClauseGroupTreemap
                rows={filteredClauseRows}
                title="Key Terms"
                groupField="clause_group"
                palette={BLUE_PALETTE}
                onCategoryClick={setSelectedClauseGroup}
              />

              <Box>
                <Typography
                  sx={{
                    mt: "0rem",
                    mb: "2rem",
                    fontSize: "1rem",
                    color: "#626367",
                    textAlign: "center",
                    lineHeight: "1rem",
                    fontWeight: "500",
                  }}
                >
                  Click a group to explore
                </Typography>
              </Box>
            </>
          ) : (
            <Card sx={{ px: "1rem", py: "1rem" }}>
              <CardContent>
                <Typography sx={{ fontSize: "0.95rem", color: "#6b7280" }}>
                  No results match the selected filters. Try removing or
                  adjusting one or more filters.
                </Typography>
              </CardContent>
            </Card>
          )}

          {hasFilteredResults && effectiveSelectedGroup && (
            <>
              {clauseHrRowsForSelection.length > 0 ? (
                <ClauseGroupTreemap
                  rows={clauseHrRowsForSelection}
                  title={`Terms in ${getClauseGroupDisplayName(effectiveSelectedGroup)}`}
                  groupField="clause_hr"
                />
              ) : (
                <Card sx={{ px: "1rem", py: "1rem" }}>
                  <CardContent>
                    <Typography sx={{ fontSize: "0.9rem", color: "#6b7280" }}>
                      No clause values found for the selected group.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <Typography
            sx={{
              mt: 2,
              fontSize: "0.85rem",
              color: "#6b7280",
              textAlign: "right",
            }}
          >
            Last updated: 09/28/2026
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}
